import type { ApiPlanStatus, ItemStatus, PlanStatus } from '@/types'

/**
 * Unified status configuration for consistent styling across the app.
 * Supports both API status formats (UPPERCASE) and display formats (Title Case).
 */

export interface StatusColors {
  bg: string
  text: string
  border: string
}

// Base status colors - used by all status components
export const STATUS_COLORS = {
  draft: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
  },
  inProgress: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
  },
  submitted: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-300',
  },
  approved: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300',
  },
  rejected: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    border: 'border-rose-300',
  },
  locked: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
  sentBack: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-300',
  },
} as const

// Status labels for display
export const STATUS_LABELS = {
  DRAFT: 'Draft',
  IN_PROGRESS: 'In Progress',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SENT_BACK: 'Sent Back',
  // Display format (Title Case) - used by AssortmentPlans grid
  Draft: 'Draft',
  'In Progress': 'In Progress',
  Submitted: 'Submitted',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Locked: 'Locked',
  'Sent Back': 'Sent Back',
} as const

// Map API/display status to internal color key
function getStatusKey(
  status: ApiPlanStatus | ItemStatus | PlanStatus
): keyof typeof STATUS_COLORS {
  const statusMap: Record<string, keyof typeof STATUS_COLORS> = {
    // API format (UPPERCASE)
    DRAFT: 'draft',
    IN_PROGRESS: 'inProgress',
    SUBMITTED: 'submitted',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    SENT_BACK: 'sentBack',
    // Display format (Title Case)
    Draft: 'draft',
    'In Progress': 'inProgress',
    Submitted: 'submitted',
    Approved: 'approved',
    Rejected: 'rejected',
    Locked: 'locked',
    'Sent Back': 'sentBack',
  }
  return statusMap[status] || 'draft'
}

/**
 * Get status colors for a given status.
 */
export function getStatusColors(
  status: ApiPlanStatus | ItemStatus | PlanStatus
): StatusColors {
  const key = getStatusKey(status)
  return STATUS_COLORS[key]
}

/**
 * Get display label for a status.
 */
export function getStatusLabel(
  status: ApiPlanStatus | ItemStatus | PlanStatus
): string {
  return STATUS_LABELS[status as keyof typeof STATUS_LABELS] || String(status)
}

/**
 * Get combined className for status badge styling.
 * Includes background, text color, and border.
 */
export function getStatusBadgeClassName(
  status: ApiPlanStatus | ItemStatus | PlanStatus
): string {
  const colors = getStatusColors(status)
  return `${colors.bg} ${colors.text} border ${colors.border}`
}

/**
 * Get combined className with hover state for interactive badges.
 */
export function getStatusBadgeHoverClassName(
  status: ApiPlanStatus | ItemStatus | PlanStatus
): string {
  const colors = getStatusColors(status)
  return `${colors.bg} ${colors.text} border ${colors.border} hover:${colors.bg}/80`
}
